import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useLiveQuery } from "dexie-react-hooks";
import { db } from "../db";
import PropTypes from 'prop-types';
import axios from "axios";
import { API } from "@/config";
import Sidebar from "@/components/Sidebar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { ArrowLeft, Download, XCircle, FileText, User } from "lucide-react";
import { formatRupees } from "../utils/currency";
import NotificationStatusBadge from "@/components/NotificationStatusBadge";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function InvoiceDetail({ token, onLogout }) {
    const { id } = useParams();
    const navigate = useNavigate();

    const [cancelReason, setCancelReason] = useState("");
    const [isCancelling, setIsCancelling] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);

    // Load invoice from local db
    const invoice = useLiveQuery(async () => {
        const inv = await db.invoices.get(id);

        // Enrich with patient
        if (inv?.patient_id) {
            const patient = await db.patients.get(inv.patient_id);
            if (patient) {
                inv.patient = patient;
            }
        }
        return inv;
    }, [id]);

    // Sync invoice from server
    useEffect(() => {
        const fetchInvoice = async () => {
            if (!id || !token) return;

            try {
                // Check if we have it locally first to avoid unnecessary calls?
                // Or always fetch to ensure freshness?
                // For now, let's fetch if we suspect it's missing or just to be safe.
                // Given the bug, we want to ensure we get it.
                // We can query DB directly here to decide.
                const localInv = await db.invoices.get(id);
                if (!localInv) {
                    const response = await axios.get(`${API}/invoices/${id}`, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    if (response.data) {
                        await db.invoices.put({ ...response.data, _synced: true });
                    }
                }
            } catch (error) {
                console.error("Failed to fetch invoice:", error);
            }
        };
        fetchInvoice();
    }, [id, token]);

    const handleDownloadPdf = async () => {
        setIsDownloading(true);
        try {
            const response = await axios.get(`${API}/invoices/${id}/pdf`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.download_url) {
                window.open(response.data.download_url, "_blank");
            } else {
                toast.error("PDF not available");
            }
        } catch (error) {
            console.error("Download error:", error);
            toast.error(error.response?.data?.detail || "Failed to download PDF");
        } finally {
            setIsDownloading(false);
        }
    };

    const handleCancel = async () => {
        if (!cancelReason.trim()) {
            toast.error("Please provide a reason for cancellation");
            return;
        }

        setIsCancelling(true);
        try {
            await axios.put(
                `${API}/invoices/${id}/cancel`,
                { reason: cancelReason },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            // Update local db
            await db.invoices.update(id, {
                status: "cancelled",
                cancelled_reason: cancelReason
            });

            // Clear invoice_id from appointments
            if (invoice?.appointment_ids?.length > 0) {
                for (const aptId of invoice.appointment_ids) {
                    await db.appointments.update(aptId, { invoice_id: null });
                }
            }

            toast.success("Invoice cancelled successfully");
        } catch (error) {
            console.error("Cancel error:", error);
            toast.error(error.response?.data?.detail || "Failed to cancel invoice");
        } finally {
            setIsCancelling(false);
        }
    };

    if (!invoice) {
        return (
            <div className="page-container">
                <Sidebar active="invoices" onLogout={onLogout} />
                <div className="main-content" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <p>Loading...</p>
                </div>
            </div>
        );
    }

    const getStatusBadge = (status) => {
        const styles = {
            finalized: { background: "#dcfce7", color: "#16a34a" },
            cancelled: { background: "#fee2e2", color: "#dc2626" },
            draft: { background: "#fef3c7", color: "#d97706" }
        };
        const style = styles[status] || styles.draft;
        return (
            <span style={{
                padding: "0.25rem 0.75rem",
                borderRadius: "9999px",
                fontSize: "0.875rem",
                fontWeight: "500",
                ...style
            }}>
                {status?.toUpperCase()}
            </span>
        );
    };

    return (
        <div className="page-container">
            <Sidebar active="invoices" onLogout={onLogout} />
            <div className="main-content min-w-0">
                <div className="header flex flex-col md:flex-row items-stretch md:items-center gap-4">
                    <div className="flex items-center gap-4 w-full">
                        <Button variant="ghost" onClick={() => navigate("/invoices")}>
                            <ArrowLeft size={20} />
                        </Button>
                        <div className="flex-1">
                            <h1 className="mb-1 text-xl md:text-2xl">{invoice.invoice_number}</h1>
                            <div className="flex items-center gap-2">
                                {getStatusBadge(invoice.status)}
                                <NotificationStatusBadge token={token} entityType="invoice" entityId={id} compact />
                            </div>
                        </div>
                    </div>
                    <div className="flex flex-col md:flex-row gap-2 w-full md:w-auto">
                        {invoice.pdf_key && (
                            <Button
                                variant="outline"
                                onClick={handleDownloadPdf}
                                disabled={isDownloading}
                                className="w-full md:w-auto"
                            >
                                <Download size={16} className="mr-2" />
                                {isDownloading ? "Loading..." : "Download PDF"}
                            </Button>
                        )}
                        {invoice.status === "finalized" && (
                            <AlertDialog>
                                <AlertDialogTrigger asChild>
                                    <Button variant="destructive" className="w-full md:w-auto">
                                        <XCircle size={16} className="mr-2" />
                                        Cancel Invoice
                                    </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                    <AlertDialogHeader>
                                        <AlertDialogTitle>Cancel Invoice</AlertDialogTitle>
                                        <AlertDialogDescription>
                                            This will mark the invoice as cancelled. The invoice number will be preserved for records.
                                            Associated appointments will become eligible for new invoices.
                                        </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <div style={{ padding: "1rem 0" }}>
                                        <Label>Cancellation Reason</Label>
                                        <Input
                                            value={cancelReason}
                                            onChange={(e) => setCancelReason(e.target.value)}
                                            placeholder="Enter reason for cancellation..."
                                        />
                                    </div>
                                    <AlertDialogFooter>
                                        <AlertDialogCancel>Keep Invoice</AlertDialogCancel>
                                        <AlertDialogAction
                                            onClick={handleCancel}
                                            disabled={isCancelling || !cancelReason.trim()}
                                            className="bg-red-600 hover:bg-red-700"
                                        >
                                            {isCancelling ? "Cancelling..." : "Cancel Invoice"}
                                        </AlertDialogAction>
                                    </AlertDialogFooter>
                                </AlertDialogContent>
                            </AlertDialog>
                        )}
                    </div>
                </div>

                {invoice.status === "cancelled" && invoice.cancelled_reason && (
                    <div style={{
                        background: "#fee2e2",
                        border: "1px solid #fecaca",
                        borderRadius: "8px",
                        padding: "1rem",
                        marginBottom: "1.5rem"
                    }}>
                        <strong>Cancellation Reason:</strong> {invoice.cancelled_reason}
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-[1000px]">
                    {/* Invoice Info */}
                    <Card>
                        <CardHeader>
                            <CardTitle style={{ fontSize: "1rem", display: "flex", alignItems: "center", gap: "0.5rem" }}>
                                <FileText size={18} />
                                Invoice Details
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div style={{ display: "grid", gap: "0.75rem" }}>
                                <div>
                                    <span style={{ color: "#64748b", fontSize: "0.875rem" }}>Invoice Number</span>
                                    <div style={{ fontWeight: "600" }}>{invoice.invoice_number}</div>
                                </div>
                                <div>
                                    <span style={{ color: "#64748b", fontSize: "0.875rem" }}>Issue Date</span>
                                    <div>{new Date(invoice.issue_date).toLocaleDateString()}</div>
                                </div>
                                <div>
                                    <span style={{ color: "#64748b", fontSize: "0.875rem" }}>Created</span>
                                    <div>{new Date(invoice.created_at).toLocaleString()}</div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Patient Info */}
                    <Card>
                        <CardHeader>
                            <CardTitle style={{ fontSize: "1rem", display: "flex", alignItems: "center", gap: "0.5rem" }}>
                                <User size={18} />
                                Patient
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div style={{ display: "grid", gap: "0.75rem" }}>
                                <div>
                                    <span style={{ color: "#64748b", fontSize: "0.875rem" }}>Name</span>
                                    <div style={{ fontWeight: "600" }}>{invoice.patient?.name || "Unknown"}</div>
                                </div>
                                <div>
                                    <span style={{ color: "#64748b", fontSize: "0.875rem" }}>Phone</span>
                                    <div>{invoice.patient?.phone || "-"}</div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Notes */}
                {invoice.notes && (
                    <Card style={{ marginTop: "1.5rem", maxWidth: "1000px" }}>
                        <CardHeader>
                            <CardTitle style={{ fontSize: "1rem" }}>Notes</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <p style={{ color: "#64748b" }}>{invoice.notes}</p>
                        </CardContent>
                    </Card>
                )}

                {/* Items */}
                <Card style={{ marginTop: "1.5rem", maxWidth: "1000px" }}>
                    <CardHeader>
                        <CardTitle style={{ fontSize: "1rem" }}>Items</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="table-container">
                            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "500px" }}>
                                <thead>
                                    <tr style={{ borderBottom: "1px solid #e2e8f0" }}>
                                        <th style={{ padding: "0.75rem", textAlign: "left" }}>Description</th>
                                        <th style={{ padding: "0.75rem", textAlign: "center", width: "80px" }}>Qty</th>
                                        <th style={{ padding: "0.75rem", textAlign: "right", width: "120px" }}>Unit Price</th>
                                        <th style={{ padding: "0.75rem", textAlign: "right", width: "120px" }}>Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {invoice.items?.map((item, index) => (
                                        <tr key={index} style={{ borderBottom: "1px solid #f1f5f9" }}>
                                            <td style={{ padding: "0.75rem" }}>{item.description}</td>
                                            <td style={{ padding: "0.75rem", textAlign: "center" }}>{item.quantity}</td>
                                            <td style={{ padding: "0.75rem", textAlign: "right" }}>{formatRupees(item.unit_price)}</td>
                                            <td style={{ padding: "0.75rem", textAlign: "right" }}>{formatRupees(item.total)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr style={{ borderTop: "1px solid #e2e8f0" }}>
                                        <td colSpan="3" style={{ padding: "0.75rem", textAlign: "right" }}>Subtotal</td>
                                        <td style={{ padding: "0.75rem", textAlign: "right" }}>{formatRupees(invoice.subtotal)}</td>
                                    </tr>
                                    {invoice.tax_amount > 0 && (
                                        <tr>
                                            <td colSpan="3" style={{ padding: "0.75rem", textAlign: "right" }}>Tax</td>
                                            <td style={{ padding: "0.75rem", textAlign: "right" }}>{formatRupees(invoice.tax_amount)}</td>
                                        </tr>
                                    )}
                                    {invoice.discount_amount > 0 && (
                                        <tr>
                                            <td colSpan="3" style={{ padding: "0.75rem", textAlign: "right", color: "#16a34a" }}>Discount</td>
                                            <td style={{ padding: "0.75rem", textAlign: "right", color: "#16a34a" }}>-{formatRupees(invoice.discount_amount)}</td>
                                        </tr>
                                    )}
                                    <tr style={{ fontWeight: "600", fontSize: "1.125rem" }}>
                                        <td colSpan="3" style={{ padding: "0.75rem", textAlign: "right" }}>Total</td>
                                        <td style={{ padding: "0.75rem", textAlign: "right" }}>{formatRupees(invoice.total_amount)}</td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    </CardContent>
                </Card>


            </div>
        </div>
    );
}

InvoiceDetail.propTypes = {
    token: PropTypes.string,
    onLogout: PropTypes.func
};
